import { track } from "../../statsig"
import { STATSIG, getLoanUpdateValue } from "../../statsig/events"
import { redirectToLogoutOnUnauthorized, UNAUTHORIZED_SESSION_MESSAGE } from "./unauthorizedRedirect"

const PARTICIPANT_SECTION = {
    profile: "yourinfo",
    residence: "residence",
    employment: "employment",
    "other-income": "otherincome",
    assets: "assets",
    "real-estate": "realestate",
    liabilities: "liabilities",
    declarations: "declarations",
    "credit-check": "creditcheck",
}

/**
 * Map borrowerside V2 PATCH path → 1003 section + partyId.
 * Returns null for non-1003 writes (GET, submit, coborrower add, …).
 */
export function parseBorrowerSide1003Patch(path = "", requestBody) {
    const declarationStepMatch = path.match(
        /\/borrowerside\/v2\/application\/participants\/([^/]+)\/declaration-steps\/([^/?]+)/
    )
    if (declarationStepMatch) {
        return {
            partyId: declarationStepMatch[1],
            section: declarationStepMatch[2],
        }
    }

    const participantMatch = path.match(
        /\/borrowerside\/v2\/application\/participants\/([^/]+)\/([^/?]+)/
    )
    if (participantMatch) {
        const partyId = participantMatch[1]
        const leaf = participantMatch[2]
        const section = PARTICIPANT_SECTION[leaf]
        if (section) {
            let resolved = section
            // declarations PATCH carries wizard step: propertymoney|finances|…
            if (leaf === "declarations" && requestBody) {
                try {
                    const parsed =
                        typeof requestBody === "string"
                            ? JSON.parse(requestBody)
                            : requestBody
                    if (parsed?.step) {
                        resolved = String(parsed.step)
                    }
                } catch {
                    // keep section=declarations
                }
            }
            return { partyId, section: resolved }
        }
        return null
    }
    if (/\/borrowerside\/v2\/application\/property(?:\?|$)/.test(path)) {
        return { partyId: "", section: "property" }
    }
    return null
}

/**
 * 1003 PATCH 成功后打 LOAN_1003_UPDATE。
 * 参数对齐 LOS：value / metadata.party_id = partyId；
 * 借款端额外：section、loan_id。
 * 跨端与 LOS 比数时用 section=yourinfo（LOS 的 profile）。
 */
export function trackLoan1003Update(path, responseBody, requestBody) {
    const parsed = parseBorrowerSide1003Patch(path, requestBody)
    if (!parsed) return false

    const loanId = responseBody?.Application?.loanId || responseBody?.Application?.loanid || ""
    const partyId = parsed.partyId || ""
    // LOS: value === party_id；无 party（如纯 property）时回退邮箱
    const value = partyId || getLoanUpdateValue()

    return track(STATSIG.LOAN.UPDATE_1003, value, {
        party_id: partyId,
        section: parsed.section,
        loan_id: loanId,
    })
}

export const borrowerSideV2Fetch = async (path, options = {}) => {
    const { skipStatsigTrack = false, ...fetchOptions } = options
    const method = (fetchOptions.method || "GET").toUpperCase()
    const token = window.sessionStorage.getItem("ZeitroA")
    const response = await fetch(window.location.origin + path, {
        cache: "no-cache",
        ...fetchOptions,
        headers: {
            Authorization: "Bearer " + token,
            Cache: "no-cache",
            "Content-Type": "application/json",
            ...(fetchOptions.headers || {}),
        },
    })

    if (response.status === 401) {
        redirectToLogoutOnUnauthorized()
        throw new Error(UNAUTHORIZED_SESSION_MESSAGE)
    }

    const body = await response.json().catch(() => ({}))
    if (!response.ok || body.Status === "Error") {
        throw new Error(body.Message || body.errMessage || "BorrowerSide V2 request failed")
    }

    if (method === "PATCH" && !skipStatsigTrack) {
        trackLoan1003Update(path, body, fetchOptions.body)
    }

    return body
}

export const getApplicationV2 = () => borrowerSideV2Fetch("/borrowerside/v2/application")

export const addCoborrowerV2 = () => (
    borrowerSideV2Fetch("/borrowerside/v2/application/coborrowers", {
        method: "POST",
        body: JSON.stringify({}),
    })
)

export const deleteCoborrowerV2 = (partyId) => (
    borrowerSideV2Fetch(`/borrowerside/v2/application/participants/${partyId}`, {
        method: "DELETE",
    })
)

export const updateParticipantProfileV2 = (partyId, profile) => (
    borrowerSideV2Fetch(`/borrowerside/v2/application/participants/${partyId}/profile`, {
        method: "PATCH",
        body: JSON.stringify(profile),
    })
)

export const updateParticipantResidenceV2 = (partyId, residence) => (
    borrowerSideV2Fetch(`/borrowerside/v2/application/participants/${partyId}/residence`, {
        method: "PATCH",
        body: JSON.stringify(residence),
    })
)

export const updatePropertyV2 = (propertyPayload, partyId = "") => (
    borrowerSideV2Fetch("/borrowerside/v2/application/property", {
        method: "PATCH",
        body: JSON.stringify({ ...propertyPayload, partyId }),
    })
)

export const selectSelectedRateV2 = (hash) => (
    borrowerSideV2Fetch("/borrowerside/v2/application/selection/selected-rate", {
        method: "PATCH",
        body: JSON.stringify({ hash }),
    })
)

export const updateSelectionV2 = (selection) => (
    borrowerSideV2Fetch("/borrowerside/v2/application/selection", {
        method: "PATCH",
        body: JSON.stringify(selection),
    })
)

export const updateParticipantEmploymentV2 = (partyId, employment) => (
    borrowerSideV2Fetch(`/borrowerside/v2/application/participants/${partyId}/employment`, {
        method: "PATCH",
        body: JSON.stringify(employment),
    })
)

export const updateParticipantOtherIncomeV2 = (partyId, otherIncome) => (
    borrowerSideV2Fetch(`/borrowerside/v2/application/participants/${partyId}/other-income`, {
        method: "PATCH",
        body: JSON.stringify(otherIncome),
    })
)

export const updateParticipantAssetsV2 = (partyId, assets) => (
    borrowerSideV2Fetch(`/borrowerside/v2/application/participants/${partyId}/assets`, {
        method: "PATCH",
        body: JSON.stringify({ assets }),
    })
)

export const updateParticipantRealEstateV2 = (partyId, realEstate, options = {}) => (
    borrowerSideV2Fetch(`/borrowerside/v2/application/participants/${partyId}/real-estate`, {
        method: "PATCH",
        body: JSON.stringify({ realEstate, ...options }),
    })
)

export const updateParticipantLiabilitiesV2 = (partyId, liabilities, options = {}) => (
    borrowerSideV2Fetch(`/borrowerside/v2/application/participants/${partyId}/liabilities`, {
        method: "PATCH",
        body: JSON.stringify({ liabilities }),
        ...options,
    })
)

export const updateParticipantDeclarationsV2 = (partyId, declarations, step) => (
    borrowerSideV2Fetch(`/borrowerside/v2/application/participants/${partyId}/declarations`, {
        method: "PATCH",
        body: JSON.stringify({ declarations, step }),
    })
)

export const updateParticipantCreditCheckV2 = (partyId, creditCheck) => (
    borrowerSideV2Fetch(`/borrowerside/v2/application/participants/${partyId}/credit-check`, {
        method: "PATCH",
        body: JSON.stringify(creditCheck),
    })
)

export const pullParticipantCreditV2 = (partyId, payload) => (
    borrowerSideV2Fetch(`/borrowerside/v2/application/participants/${partyId}/credit-pull`, {
        method: "POST",
        body: JSON.stringify(payload),
    })
)

export const completeParticipantDeclarationStepV2 = (partyId, step) => (
    borrowerSideV2Fetch(`/borrowerside/v2/application/participants/${partyId}/declaration-steps/${step}`, {
        method: "PATCH",
        body: JSON.stringify({}),
    })
)

export const notifyLoanOfficerApplicationFinishedV2 = async () => {
    const token = window.sessionStorage.getItem("ZeitroA")
    const response = await fetch(window.location.origin + "/borrower/autocreatetasksandnotifylo", {
        method: "GET",
        headers: {
            Authorization: "Bearer " + token,
            Cache: "no-cache",
        },
    })
    if (!response.ok) {
        throw new Error("Failed to notify loan officer")
    }
}

export const submitApplicationV2 = (partyId = "") => (
    borrowerSideV2Fetch("/borrowerside/v2/application/submit", {
        method: "POST",
        body: JSON.stringify({ partyId }),
    })
)
